/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TrashIcon } from "@heroicons/react/24/outline";
import Avatar from "../../components/Avatar";
import { useLanguage } from "../../contexts/LanguageContext";
import { useStore } from "../../contexts/StoreContext";
import { customFieldRows, formatCustomFieldValue } from "../../utils/customFields";
import { formatNumber, formatRelative } from "../dashboard/format";
import { Skeleton } from "../dashboard/components/ui";
import {
  balanceUnit,
  formatBalanceFigure,
  formatCalendarDay,
  formatDate,
  formatDelta,
  kindLabelKey,
  phoneText,
  segmentForLastVisit,
  visitStats,
  withBalanceAfter,
} from "./customerFormat";
import { customerQueryKey, customerRef, loadCustomer } from "./loadCustomer";
import { CloseButton, DeltaPill, Pill, SegmentPill } from "./ui";
import { buttonClass } from "./styles";

const RECENT = 5;

function Stat({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-[10px] bg-gray-50 px-3 py-2.5">
      <span className="text-xs text-gray-600">{label}</span>
      <span className="text-lg font-semibold text-gray-900 tabular-nums">{children}</span>
    </div>
  );
}

export function TierProgress({ progress, hideName = false }) {
  const { t } = useLanguage();
  const required = Number(progress.cycles_required) || 0;
  const completed = Number(progress.cycles_completed) || 0;
  const ratio = required > 0 ? Math.min(1, completed / required) : 1;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {/* Hidden where a heading or pill right above already names the card. */}
        {!hideName && (
          <span className="text-sm font-semibold text-gray-900">{progress.current_pass_name}</span>
        )}
        <span className="text-[13px] text-gray-600">
          {t("customers.show.tierProgressValue", {
            completed,
            required,
            next: progress.next_pass_name,
          })}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
        <div className="h-full rounded-full bg-indigo-600" style={{ width: `${ratio * 100}%` }} />
      </div>
    </div>
  );
}

// The selected row, next to the list: contact details from the row itself,
// and recent activity, rewards and tier from the full record (the same
// cache entry the profile page uses).
const CustomerPanel = forwardRef(function CustomerPanel(
  { customer, showSegments, canManage, onClose, onSendPass, onEdit, onDelete },
  ref,
) {
  const { t, language } = useLanguage();
  const { selectedStore } = useStore();
  const storeId = selectedStore?.id;
  const customerRefValue = customerRef(customer);
  const detail = useQuery({
    queryKey: customerQueryKey(storeId, customerRefValue),
    queryFn: () => loadCustomer(storeId, customerRefValue),
    enabled: !!storeId && !!customerRefValue,
  });

  const unit = balanceUnit(customer.pass?.type);
  const money = { t, language, currency: selectedStore?.currency };
  const now = new Date();
  const segment = segmentForLastVisit(customer.last_visit_at, now);
  const name = customer.name || t("customers.unnamed");
  const history = withBalanceAfter(detail.data?.transactions);
  const recent = history.slice(-RECENT).reverse();
  const { rewards } = visitStats(detail.data?.transactions);
  const fields = customFieldRows(customer.custom_fields, selectedStore?.custom_fields);

  const rows = [
    [t("customers.table.email"), customer.email || t("customers.panel.noEmail")],
    [t("customers.table.phone"), phoneText(customer) || "—"],
    [t("customers.table.birthday"), formatCalendarDay(customer.birthday, language, { day: "numeric", month: "long" }) || "—"],
    ...fields.map((field) => [field.label, formatCustomFieldValue(field.value, t)]),
    [t("customers.panel.expiry"), customer.expires_at ? formatDate(customer.expires_at, language) : t("customers.panel.noExpiry")],
    [t("customers.panel.marketing"), customer.is_marketing_opted_in ? t("customers.panel.agreed") : t("customers.panel.notAgreed")],
  ];

  return (
    <aside
      ref={ref}
      aria-label={t("customers.panel.label")}
      className="flex min-w-0 flex-[1_1_340px] scroll-mt-4 flex-col gap-[18px] rounded-[14px] border border-gray-200 bg-white p-5 lg:sticky lg:top-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={customer.name || customer.email} className="h-[52px] w-[52px] text-[17px]" />
          <div className="flex min-w-0 flex-col gap-1.5">
            <h2 className="m-0 truncate text-lg font-semibold text-gray-900">{name}</h2>
            <span className="flex flex-wrap items-center gap-1.5">
              {customer.pass && <Pill>{customer.pass.name}</Pill>}
              {showSegments && <SegmentPill segment={segment} />}
            </span>
          </div>
        </div>
        <CloseButton onClick={onClose} label={t("customers.panel.close")} />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat label={t(`customers.units.${unit}.label`)}>
          {formatBalanceFigure(customer.point, unit, money)}
        </Stat>
        <Stat label={t("customers.panel.visits")}>{formatNumber(customer.visits ?? 0, language)}</Stat>
        <Stat label={t("customers.panel.rewards")}>
          {detail.isLoading ? <Skeleton className="mt-1 h-5 w-8" /> : formatNumber(rewards, language)}
        </Stat>
      </div>

      {detail.data?.tier_progress && (
        <TierProgress
          progress={detail.data.tier_progress}
          hideName={detail.data.tier_progress.current_pass_name === customer.pass?.name}
        />
      )}

      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 text-sm">
        {rows.map(([label, value], index) => (
          <div key={`${label}-${index}`} className="contents">
            <dt className="text-gray-600">{label}</dt>
            <dd className="m-0 [overflow-wrap:anywhere] text-gray-900">{value}</dd>
          </div>
        ))}
      </dl>

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="m-0 text-sm font-semibold text-gray-900">{t("customers.panel.recentActivity")}</h3>
          <Link
            to={`/customers/${encodeURIComponent(customerRefValue)}`}
            className="text-[13px] font-semibold text-indigo-700 hover:text-indigo-800"
          >
            {t("customers.panel.openProfile")}
          </Link>
        </div>
        {detail.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : detail.isError ? (
          <p className="m-0 text-[13px] text-gray-600">{t("customers.panel.activityError")}</p>
        ) : recent.length === 0 ? (
          <p className="m-0 text-[13px] text-gray-600">{t("customers.panel.noActivity")}</p>
        ) : (
          <ul className="m-0 flex list-none flex-col p-0">
            {recent.map((tx) => {
              const when = formatRelative(tx.created_at, language, now);
              return (
                <li key={tx.id} className="flex items-center justify-between gap-3 border-t border-gray-100 py-2 first:border-t-0">
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-[13px] font-medium text-gray-900">{t(kindLabelKey(tx.kind))}</span>
                    <span className="truncate text-xs text-gray-600">
                      {tx.user?.name ? `${t("customers.panel.by", { name: tx.user.name })} · ${when}` : when}
                    </span>
                  </span>
                  <DeltaPill value={Number(tx.point) || 0}>{formatDelta(tx.point, unit, money)}</DeltaPill>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={onSendPass} className={`${buttonClass.primary} col-span-2`}>
          {t("customers.sendPass.button")}
        </button>
        {/* Editing and deleting are admin-only on the API (authorize_admin). */}
        {canManage && (
          <>
            <button type="button" onClick={onEdit} className={buttonClass.small}>
              {t("customers.panel.edit")}
            </button>
            {/* Short visible label so it fits the half-width slot; the
                accessible name keeps the full "Delete customer". */}
            <button
              type="button"
              onClick={onDelete}
              aria-label={t("customers.delete.confirm")}
              className={buttonClass.dangerOutline}
            >
              <TrashIcon className="h-4 w-4" aria-hidden="true" />
              {t("common.delete")}
            </button>
          </>
        )}
      </div>
    </aside>
  );
});

export default CustomerPanel;
